// dateOrder.js
// How a file's text dates are read. A date such as 03/04/2026 is 3 April read
// day-first (UK) and 4 March read month-first (US). Cleaning's DateOrder process
// decides one order for the file and one for each unit, null while the unit
// inherits the file's order (the file's `processes.date_order` record), and each
// unit document carries the order its dates are read with, `date_order`
// (backend: ingestion/cleaning/file_processes.py).

export const DAY_FIRST = 'day_first';
export const MONTH_FIRST = 'month_first';

export const DATE_ORDER_LABELS = {
  [DAY_FIRST]: 'UK (day/month)',
  [MONTH_FIRST]: 'US (month/day)',
};

// Twin of the backend's _AMBIGUOUS_DATE_RE: day and month in either order, then the year.
const DAY_MONTH_TEXT = /^\s*(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s+.*)?$/;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// The calendar date named by `day`, `month` and `year`, or null when there is none
// (month 13, 31 April, day 0).
function calendarDate(day, month, year) {
  if (month < 1 || month > 12 || day < 1) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCDate() === day ? { day, month, year } : null;
}

// Both readings of d/m/y or m/d/y text, each null when that reading is not a real
// date; null when the value is not such text. A two-digit year is read as 20yy.
function dateReadings(value) {
  if (typeof value !== 'string') return null;
  const match = DAY_MONTH_TEXT.exec(value);
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]) + (match[3].length === 2 ? 2000 : 0);
  return {
    [DAY_FIRST]: calendarDate(first, second, year),
    [MONTH_FIRST]: calendarDate(second, first, year),
  };
}

// Text that is a real, different date in each order, such as 03/04/2026.
export function isAmbiguousDayMonth(value) {
  const readings = dateReadings(value);
  if (!readings || !readings[DAY_FIRST] || !readings[MONTH_FIRST]) return false;
  return readings[DAY_FIRST].day !== readings[MONTH_FIRST].day
    || readings[DAY_FIRST].month !== readings[MONTH_FIRST].month;
}

// The first value in these rows (objects of cells) that reads differently in each
// order, or null.
export function firstAmbiguousDate(rows) {
  for (const row of rows || []) {
    for (const value of Object.values(row || {})) {
      if (isAmbiguousDayMonth(value)) return value.trim();
    }
  }
  return null;
}

// "03/04/2026 will be read as 3 April 2026" under `order`.
export function describeDateReading(value, order) {
  const reading = dateReadings(value)?.[order];
  if (!reading) return '';
  return `${value.trim()} will be read as ${reading.day} ${MONTH_NAMES[reading.month - 1]} ${reading.year}`;
}
